import { liveStatsVuexGetters, LiveStatsTopics, CountingSchemes } from '@ahaslides-product/frontend-toolkit'
import {
  isFeatureEnabled,
  isPresentationCreatedAfterTurnOnFlag,
  isPresentationResetAfterTurnOnFlag,
  comparePresentationCreatedDateWithFlag,
} from '@/vuex/modules/featureToggles.module'
import config from '@/config'
import { isClonedFromOnboardingTemplate } from './onboardingTemplateClone'

const _getters = {
  ...liveStatsVuexGetters,

  canUseKvSyncLiveStats: (_, __, rootState, rootGetters) => {
    if (!rootState?.presentation?.id) {
      return false
    }
    return isFeatureEnabled(rootGetters, 'kv-sync-emqx-for-live-stats') && (isPresentationCreatedAfterTurnOnFlag(rootGetters, 'kv-sync-emqx-for-live-stats') || isPresentationResetAfterTurnOnFlag(rootState.presentation, 'kv-sync-emqx-for-live-stats'))
  },
  // SYST-74: share/casting cold-load variant of `canUseKvSyncLiveStats`, which can't run
  // here because it short-circuits on `presentation.id` and the endpoint choice happens
  // before the deck loads. Answers the same flag/date gates from the already-fetched
  // flag-info, only for the share code asked about — a mismatch or missing gate falls
  // through to the unbounded endpoint, the safe default the v1 renderer needs. No fallback
  // to `canUseKvSyncLiveStats` on purpose: it reads stale `presentation` state. See PR #5108.
  canUseKvSyncLiveStatsForShareCode: (_, __, rootState, rootGetters) => (shareCode) => {
    const flagInfo = rootState?.featureToggles?.presentationFlagInfoByShareCode
    if (!shareCode || flagInfo?.shareCode !== shareCode) return false
    if (!flagInfo.createdAt && !flagInfo.resetTimeStamp) return false
    return isFeatureEnabled(rootGetters, 'kv-sync-emqx-for-live-stats') && (comparePresentationCreatedDateWithFlag(flagInfo, 'kv-sync-emqx-for-live-stats') || isPresentationResetAfterTurnOnFlag(flagInfo, 'kv-sync-emqx-for-live-stats'))
  },
  isClonedFromTemplateAndNotResetYet: (_, __, rootState) => {
    return isClonedFromOnboardingTemplate(rootState.presentation, rootState.presenterLocale, config.env) && !isPresentationResetAfterTurnOnFlag(rootState.presentation, 'kv-sync-emqx-for-live-stats')
  },
  canUseWordCloudEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUsePollEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUseScaleEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUseReactionEmqx: (_, getters) => {
    return getters.canUseKvSyncLiveStats
  },
  canUseQuizLiveCountsEmqx: (_, getters, __, rootGetters) => {
    // Requires answer v2, since per-option counts only reach the pipeline when
    // quiz answers go through liveproxy /answer/create/v2.
    return getters.canUseKvSyncLiveStats
      && getters.canUseLiveStatsForQuizV2
      && isFeatureEnabled(rootGetters, 'AHA-45913-pick-answer-votes-count')
  },

  pollVoteCount: (state, getters, rootState) => {
    if (getters.canUsePollEmqx) {
      return getters.answererCountPerQuestion(rootState.slideActive.id, 0, rootState.slideActive.version)
    }
    return rootState.slideActive.multipleCountAnswer
  },
  scaleVoteCount: (state, getters, rootState) => {
    if (getters.canUseScaleEmqx) {
      return (rootState?.slideActive?.SlideOptions?.length) ? (
        Math.ceil(
          getters.answerCountPerActivity(rootState.slideActive.id, rootState.slideActive.version) /
          rootState.slideActive.SlideOptions.length,
        ))
        : 0
    }
    return rootState.slideActive.multipleCountAnswer
  },
  wordCloudVoteCount: (state, getters, rootState) => {
    if (getters.canUseWordCloudEmqx) {
      return getters.answerCountPerActivity(rootState.slideActive.id, rootState.slideActive.version)
    }
    return rootState.slideActive?.SlideOptions?.length
  },
  canUseWordCloudLiveProxy: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'liveproxy-submit-wordcloud')
  },
  canUseLiveStatsForQuizV2: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'livestats-quiz-submission-v2')
  },
  canRetireLiveApi: (_, getters, ___, rootGetters) => {
    return getters.canUseLiveStatsForQuizV2 && isFeatureEnabled(rootGetters, 'retire-live-api')
  },
  canGetReactionCountFromAhaSync: (_, getters, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'AHA-37161-use-reaction-counts-ahasync')
            && getters.canUseKvSyncLiveStats
  },
  correctAnswerCountPerActivityV2: (state) => {
    return (activityId, activityVersion) => {
      return Number(state[`categorise-v2/${LiveStatsTopics.correctAnswerCountPerActivityTopic(activityId, activityVersion)}`] || 0)
    }
  },
  canAddDeltaForAhaSync: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'AHA-37132-get-with-delta')
  },
  incorrectAnswerCountPerActivityV2: (state) => {
    return (activityId, activityVersion) => {
      return Number(state[`categorise-v2/${LiveStatsTopics.incorrectAnswerCountPerActivityTopic(activityId, activityVersion)}`] || 0)
    }
  },
  correctAnswerCountPerAnswerValueV2: (state) => {
    return (activityId, activityVersion, questionId, answerValue) => {
      return Number(state[`categorise-v2/${LiveStatsTopics.correctAnswerCountPerAnswerValueTopic(activityId, activityVersion, questionId, answerValue)}`] || 0)
    }
  },
  incorrectAnswerCountPerAnswerValueV2: (state) => {
    return (activityId, activityVersion, questionId, answerValue) => {
      return Number(state[`categorise-v2/${LiveStatsTopics.incorrectAnswerCountPerAnswerValueTopic(activityId, activityVersion, questionId, answerValue)}`] || 0)
    }
  },
  canReadAnswertextFromCounting: (_, getters, rootState, rootGetters) => {
    return getters.canUseKvSyncLiveStats
      && isFeatureEnabled(rootGetters, 'AHA-43140-answertext-dual-read')
      && Boolean(rootState?.presentation?.id)
  },

  canReadAnswerscoreHistogramFromCounting: (_, getters, rootState, rootGetters) => {
    return getters.canUseKvSyncLiveStats
      && isFeatureEnabled(rootGetters, 'AHA-43140-answerscore-histogram-dual-read')
      && Boolean(rootState?.presentation?.id)
  },

  answerCountPerActivity: (state, getters, rootState, rootGetters) => (activityId, version) => {
    if (getters.canReadAnswertextFromCounting) {
      return rootGetters['counting/total'](CountingSchemes.answerCountPerActivity({
        presentationId: rootState.presentation.id,
        slideId: activityId,
        slideVersion: version,
      })) ?? 0
    }
    return liveStatsVuexGetters.answerCountPerActivity(state)(activityId, version)
  },

  answerCountPerAnswerValue: (state, getters, rootState, rootGetters) => (
    activityId, questionId, version, answerValue,
  ) => {
    if (getters.canReadAnswertextFromCounting) {
      return rootGetters['counting/total'](CountingSchemes.answerCountPerAnswerValue({
        presentationId: rootState.presentation.id,
        slideId: activityId,
        slideVersion: version,
        questionId,
        answerValue,
      })) ?? 0
    }
    return liveStatsVuexGetters.answerCountPerAnswerValue(state)(activityId, questionId, version, answerValue)
  },

  scoreCountPerAnswerValue: (state, getters, rootState, rootGetters) => (
    activityId, questionId, version, score,
  ) => {
    if (getters.canReadAnswerscoreHistogramFromCounting) {
      return rootGetters['counting/total'](CountingSchemes.answerCountPerAnswerValue({
        presentationId: rootState.presentation.id,
        slideId: activityId,
        slideVersion: version,
        questionId,
        answerValue: score,
      })) ?? 0
    }
    return liveStatsVuexGetters.answerCountPerAnswerValue(state)(activityId, questionId, version, score)
  },

  liveStatsVersionPrefix: () => {
    return config.newKafkaClusterPrefix || 'ST2'
  },
  canUnsubscribeLiveStatsOnDestroy: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-unsubscribe-kvsync')
  },
}

export default _getters
